import React from 'react'
import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSection,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from 'components/ModalSheet'
import {
  Form,
  FormColumn,
  FormColumns,
  FormRow,
  NumericInputContainer,
} from 'components/inputs'
import Button from 'components/Button'
import { NotificationBar } from 'components/NotificationBar'
import {
  BulkBundlePriceList,
  BundlePriceItem,
} from 'admin/bundle/components/BulkBundlePriceList'
import { BulkApplyDiscountViewModel } from './BulkApplyDiscountViewModel'
import styles from './BulkApplyDiscountDialog.sass'

interface Props {
  vm: BulkApplyDiscountViewModel
}

export default observer(function BulkApplyDiscountDialog({ vm }: Props) {
  return (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={vm.showing}
      onRequestClose={vm.close}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Apply discount</ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <ModalSheetSection spaced>
          <Form id="bulkApplyDiscountForm" onSubmit={vm.submit}>
            <FormRow>
              <FormColumns>
                <FormColumn>
                  <NumericInputContainer
                    autoFocus
                    mode="live"
                    precision={0}
                    placeholder="0"
                    label="Discount percentage"
                    field="percentOff"
                    model={vm}
                    error={vm.validation.getError('percentOff')}
                    data-test="bulk-apply-discount-percent-input"
                    help="Applied to each bundle's current price. Service item prices are not affected."
                  />
                </FormColumn>
              </FormColumns>
            </FormRow>
          </Form>
        </ModalSheetSection>

        <BulkBundlePriceList rightHeader="New price">
          {vm.previews.map((preview) => {
            const isNegative = preview.newPrice < 0

            return (
              <BundlePriceItem
                key={preview.bundle.Id}
                name={preview.bundle.name}
                currentPrice={preview.currentPrice}
                dataTest={`bulk-apply-discount-preview-${preview.bundle.name}`}
                right={
                  vm.hasDiscount && (
                    <span
                      className={
                        isNegative ? styles.negativePrice : styles.newPrice
                      }
                    >
                      {formatPrice(preview.newPrice)}
                      {isNegative && ' (below $0)'}
                    </span>
                  )
                }
              />
            )
          })}
        </BulkBundlePriceList>
      </ModalSheetContent>

      {vm.negativePriceWarning && (
        <NotificationBar
          centered
          type="error"
          dataTest="bulk-apply-discount-warning"
        >
          {vm.negativePriceWarning}
        </NotificationBar>
      )}

      <ModalSheetFooter>
        {!vm.hasEligibleBundles ? (
          <ModalSheetFooterSection>
            All selected bundles are archived and can’t be discounted.
          </ModalSheetFooterSection>
        ) : (
          vm.hasArchivedBundles && (
            <ModalSheetFooterSection>
              {vm.archivedCount} archived{' '}
              {vm.archivedCount === 1 ? 'bundle' : 'bundles'} excluded.
            </ModalSheetFooterSection>
          )
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            disabled={vm.submit.pending}
            dataTest="bulk-apply-discount-close-button"
          >
            Close
          </Button>

          <Button
            submit
            form="bulkApplyDiscountForm"
            type="primary"
            loading={vm.submit.pending}
            disabled={!vm.canApply || vm.submit.pending}
            dataTest="bulk-apply-discount-submit-button"
          >
            Apply discount
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

function formatPrice(value: number) {
  return value < 0 ? `-$${Math.abs(value).toFixed(2)}` : `$${value.toFixed(2)}`
}
